import { DraggableAttributes, DragOverlay } from "@dnd-kit/core";
import clsx from "clsx";
import { useState } from "react";

import {
  Button,
  SortableItem,
  SortableList,
  Tooltip,
} from "@smart/itops-sb-design-system-dom";

import { optionContainerClasses } from "./class-names";
import { DraggableOptionItem, OptionsSharedProps } from "./option-item";
import { OtherOptionItem } from "./other-item";

const MAX_OPTIONS_TO_DISPLAY = 8;

type EditOptionsProps = OptionsSharedProps & {
  addOption: (options: "text" | "other") => void;
  moveOption: (sourceIndex: number, destinationIndex: number) => Promise<void>;
  openOptionModal?: () => void;
  isShowingOtherOption: boolean;
  isOptionsFixed?: boolean;
  hasDynamicOptions?: boolean;
  tooltips?: {
    addOptionButton?: string;
    addOtherButton?: string;
  };
  disabled?: boolean;
};

export const EditOptions = ({
  optionsFields,
  type,
  addOption,
  isShowingOtherOption,
  isOptionsFixed,
  tooltips,
  removeOption,
  updateOption,
  moveOption,
  openOptionModal,
  hasDynamicOptions,
  disabled,
}: EditOptionsProps) => {
  const [newOptionIndex, setNewOptionIndex] = useState<number>();
  const [activeId, setActiveId] = useState<string>();
  const displayOverflow =
    openOptionModal && optionsFields.length > MAX_OPTIONS_TO_DISPLAY;

  if (hasDynamicOptions) {
    return (
      <div className={clsx(optionContainerClasses, "px-[0.8rem]")}>
        <span className="text-ui">Options will be dynamically generated</span>
      </div>
    );
  }

  return (
    <div>
      <SortableList
        itemKeys={optionsFields.map((option) => option.id)}
        onDragStart={({ active }) => {
          setActiveId(active.id as string);
        }}
        onDragEnd={async ({ active, over }) => {
          setActiveId(undefined);
          const activeIndex = optionsFields.findIndex(
            (o) => o.id === active.id,
          );
          const overIndex = optionsFields.findIndex((o) => o.id === over?.id);
          if (overIndex >= 0 && activeIndex !== overIndex)
            await moveOption(activeIndex, overIndex);
        }}
      >
        <div className="flex flex-col mx-0 mb-[0.4rem] px-[0.4rem]">
          {(displayOverflow
            ? optionsFields.slice(0, MAX_OPTIONS_TO_DISPLAY)
            : optionsFields
          ).map((option, index) => (
            <SortableItem
              key={option.id}
              id={option.id}
              disabled={disabled || optionsFields.length <= 1}
            >
              {({ dragHandleProps, isDragging }) => (
                <DraggableOptionItem
                  index={index}
                  dragHandleProps={dragHandleProps}
                  type={type}
                  optionsFields={optionsFields}
                  removeOption={removeOption}
                  updateOption={async (
                    optionIndex: number,
                    label: string | undefined,
                  ) => {
                    setNewOptionIndex(undefined);
                    await updateOption(optionIndex, label);
                  }}
                  newOptionIndex={newOptionIndex}
                  isDragging={isDragging}
                  disabled={disabled}
                />
              )}
            </SortableItem>
          ))}
        </div>
        <DragOverlay>
          {activeId ? (
            <div className="cursor-grab">
              <DraggableOptionItem
                index={optionsFields.findIndex((o) => o.id === activeId)}
                dragHandleProps={{
                  attributes: {} as DraggableAttributes,
                  listeners: {},
                }}
                type={type}
                optionsFields={optionsFields}
                removeOption={async () => {}}
                updateOption={async () => {}}
                newOptionIndex={undefined}
                isDragging={false}
                disabled={disabled}
              />
            </div>
          ) : null}
        </DragOverlay>
      </SortableList>
      <OtherOptionItem
        type={type}
        isShowingOtherOption={isShowingOtherOption}
        removeOption={removeOption}
        disabled={disabled}
      />
      {displayOverflow ? (
        <Button
          text={`View or edit all ${optionsFields.length} options`}
          variant="highlight"
          onClick={openOptionModal}
          className="ml-[4px]"
        />
      ) : (
        <div className="flex gap-small px-[0.4rem] my-[1.6rem]">
          <Tooltip
            className="flex items-center"
            text={tooltips?.addOptionButton}
            placement="top-end"
            offsetBy={7}
            variant="paragraphy"
          >
            <Button
              text="Option"
              leftIcon="regularPlus"
              variant="primarySubtle"
              onClick={() => {
                addOption("text");
                setNewOptionIndex(optionsFields.length);
              }}
              size="small"
              dataTestId="add-option-button"
              disabled={isOptionsFixed || disabled}
            />
          </Tooltip>
          <Tooltip
            className="flex items-center"
            text={tooltips?.addOtherButton}
            placement="top-end"
            offsetBy={7}
            variant="paragraphy"
          >
            <Button
              text={'"Other"'}
              leftIcon="regularPlus"
              variant="primarySubtle"
              onClick={() => {
                addOption("other");
              }}
              disabled={isOptionsFixed || isShowingOtherOption || disabled}
              size="small"
              dataTestId="add-other-option-button"
            />
          </Tooltip>
        </div>
      )}
    </div>
  );
};
